import { useRoute, useRouter } from 'vue-router'
import { ref, computed } from 'vue'
import { defineStore, acceptHMRUpdate } from 'pinia'

import { useAccountStore } from 'src/stores/account'
import { usePoliciesQuery } from 'src/composables/policy/query/usePoliciesQuery'

import {
  buildOwnedProductIndex,
  filterPolicyRows,
  getDefaultFilter,
  getDefaultSort,
  getDirectoryCounts,
  getOwnedProductRecord,
  getPolicyGroupBy,
  sortPolicyRows,
} from 'src/services/helpers/policy.helper'
import { DEFAULT_FILTER_SORT, POLICY_TYPES } from 'src/services/consts/policy.const'
import {
  getFilterFromQuery,
  getSortAndPaginationFromQuery,
  isSameTableState,
  sanitizeQuery,
  paginateItems,
} from 'src/services/app/util.service'

export const usePoliciesStore = defineStore('policies', () => {
  const route = useRoute()
  const router = useRouter()
  const accountStore = useAccountStore()

  const {
    policyIndex,
    policyRows: rowsByType,
    showAllowList,
    showBlockList,
    isPoliciesLoading,
    isPoliciesRefetching,
    policiesError,
    refetchPolicies,
  } = usePoliciesQuery()

  const filterBy = ref(getFilterFromQuery(getDefaultFilter(), route.query))
  const tableState = ref(getSortAndPaginationFromQuery(getDefaultSort(), route.query))

  const visibleTypes = computed(() => {
    const types = []
    if (showAllowList.value) types.push(POLICY_TYPES.ALLOW_LIST)
    if (showBlockList.value) types.push(POLICY_TYPES.BLOCK_LIST)
    return types
  })
  const activeType = computed(() =>
    visibleTypes.value.includes(filterBy.value.status)
      ? filterBy.value.status
      : (visibleTypes.value[0] ?? null)
  )
  const allRows = computed(() => rowsByType.value?.[activeType.value] || [])
  const filteredRows = computed(() => filterPolicyRows(allRows.value, filterBy.value))
  const groupBy = computed(() => getPolicyGroupBy(tableState.value.sortBy))
  const ownedProductIndex = computed(() =>
    buildOwnedProductIndex({
      tickets: accountStore.accountData?.tickets,
      approvals: accountStore.accountData?.approvals,
    })
  )
  const rowsToShow = computed(() => {
    const sorted = sortPolicyRows(filteredRows.value, groupBy.value, tableState.value.descending)
    return paginateItems(sorted, tableState.value.page, tableState.value.rowsPerPage).map((row) => {
      const owned = getOwnedProductRecord({
        index: ownedProductIndex.value,
        companyId: row.companyId,
        productId: row.productId,
      })
      return {
        ...row,
        ownedState: owned?.ownedState ?? null,
        approvalId: owned?.approvalId ?? null,
        ticketId: owned?.ticketId ?? null,
      }
    })
  })
  const directoryCounts = computed(() => getDirectoryCounts(rowsByType.value))
  const hasActiveFilters = computed(() => !!filterBy.value.search)

  function syncStateFromQuery(query) {
    filterBy.value = getFilterFromQuery(getDefaultFilter(), query)

    if (isPoliciesLoading.value || policiesError.value) return

    const totalItems = filteredRows.value.length
    const defaultSort = getDefaultSort()
    const nextTableState = getSortAndPaginationFromQuery(defaultSort, query, { totalItems })

    if (!isSameTableState(tableState.value, nextTableState)) {
      tableState.value = nextTableState
    }

    const urlPage = parseInt(query.page) || defaultSort.page
    if (nextTableState.page !== urlPage) {
      const combinedParams = { ...filterBy.value, ...nextTableState }
      const cleanQuery = withOverlayQuery(sanitizeQuery(combinedParams, DEFAULT_FILTER_SORT))
      router.replace({ query: cleanQuery })
    }
  }

  function resetFiltersState() {
    filterBy.value = getDefaultFilter()
    tableState.value = getDefaultSort()
  }

  function updateQuery(fieldsToUpdate) {
    // Updating the query will trigger the watch and update the state
    if (Object.keys(fieldsToUpdate).length === 0) return
    const shouldPushToHistory =
      fieldsToUpdate.status ||
      (fieldsToUpdate.page && fieldsToUpdate.page !== tableState.value.page)
    const combinedParams = {
      ...filterBy.value,
      ...tableState.value,
      ...fieldsToUpdate,
    }

    const cleanQuery = withOverlayQuery(sanitizeQuery(combinedParams, DEFAULT_FILTER_SORT))

    const action = shouldPushToHistory ? 'push' : 'replace'
    router[action]({ query: cleanQuery })
  }

  function withOverlayQuery(cleanQuery) {
    const ticketId = route.query.ticketId
    const approvalId = route.query.approvalId
    if (ticketId) cleanQuery.ticketId = ticketId
    if (approvalId) cleanQuery.approvalId = approvalId
    return cleanQuery
  }

  function updateFilters(filterToUpdate) {
    updateQuery({ ...filterToUpdate, page: 1 })
  }

  function updateTableState(stateToUpdate) {
    if (stateToUpdate.sortBy) {
      updateQuery({ ...stateToUpdate, sortBy: getPolicyGroupBy(stateToUpdate.sortBy) })
      return
    }
    updateQuery(stateToUpdate)
  }

  function clearFilters() {
    // Only clear filters and page - keeps sort and directory
    const currentStatus = filterBy.value.status
    const newFilter = { ...getDefaultFilter(), status: currentStatus }
    updateQuery({ ...newFilter, page: 1 })
  }

  function resetFilters() {
    // Resets everything
    updateQuery(DEFAULT_FILTER_SORT)
    filterBy.value = getDefaultFilter()
    tableState.value = getDefaultSort()
  }

  return {
    // State
    filterBy,
    tableState,

    // Computed
    visibleTypes,
    activeType,
    allRows,
    filteredRows,
    groupBy,
    rowsToShow,
    directoryCounts,
    hasActiveFilters,
    policyIndex,
    showAllowList,
    showBlockList,

    isPoliciesLoading,
    isPoliciesRefetching,
    policiesError,
    refetchPolicies,

    // Actions
    syncStateFromQuery,
    resetFiltersState,
    updateQuery,
    updateTableState,
    updateFilters,
    clearFilters,
    resetFilters,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePoliciesStore, import.meta.hot))
}
